import type { Company } from '@/app/types/Company';

export type CompanySource = 'company';

export interface ParsedCompanySelectionToken {
  source: CompanySource;
  id: number;
  token: string;
}

const COMPANY_PREFIX_PATTERN = /^company\s*:\s*(\d+)$/i;
const UNSUPPORTED_PREFIX_PATTERN = /^(?:ts|theirstack)\s*:/i;

const toInteger = (value: unknown): number | null => {
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value <= 0) return null;
    return Math.trunc(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (!/^[1-9]\d*$/.test(trimmed)) return null;
    return Number(trimmed);
  }
  return null;
};

export function parseCompanySelectionToken(raw: unknown): ParsedCompanySelectionToken | null {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;

    if (UNSUPPORTED_PREFIX_PATTERN.test(trimmed)) return null;

    const companyMatch = trimmed.match(COMPANY_PREFIX_PATTERN);
    if (companyMatch) {
      const parsed = toInteger(companyMatch[1]);
      if (parsed === null || parsed <= 0) return null;
      return { source: 'company', id: parsed, token: `company:${parsed}` };
    }

    const parsed = toInteger(trimmed);
    if (parsed === null) return null;
    return { source: 'company', id: parsed, token: `company:${parsed}` };
  }

  const parsed = toInteger(raw);
  if (parsed === null) return null;
  return { source: 'company', id: parsed, token: `company:${parsed}` };
}

export function getCompanySelectionToken(
  company:
    | (Partial<
        Pick<
          Company,
          'id' | 'idSource' | 'theirstackId' | 'canonicalCompanyId' | 'companyId'
        >
      >)
    | null
    | undefined,
): string | null {
  if (!company) return null;

  const parsed = parseCompanySelectionToken(
    (company as any).canonicalCompanyId ?? (company as any).companyId ?? company.id,
  );

  if (parsed) {
    return `company:${parsed.id}`;
  }

  return null;
}

export function getCompanyListSelectionToken(
  company:
    | Partial<Pick<Company, 'id' | 'idSource' | 'theirstackId'>>
    | null
    | undefined,
): string | null {
  return getCompanySelectionToken(company);
}

/**
 * All identity keys a company row can be matched under, most specific first.
 *
 * Different surfaces may carry both `id` and `canonicalCompanyId` while data is
 * hydrating. Match on canonical company aliases so list/search patches update
 * the same row.
 */
export function getCompanyIdentityAliases(
  company:
    | (Partial<Pick<Company, 'id' | 'idSource' | 'theirstackId'>> & {
        source?: Company['idSource'];
        canonicalCompanyId?: number | string | null;
      })
    | null
    | undefined,
): string[] {
  if (!company) return [];

  const aliases: string[] = [];
  const push = (key: string | null | undefined) => {
    if (key && !aliases.includes(key)) {
      aliases.push(key);
    }
  };

  push(
    getCompanySelectionToken({
      id: company.id,
      idSource: 'company',
      theirstackId: null,
      canonicalCompanyId: toInteger(company.canonicalCompanyId),
      companyId: (company as any).companyId,
    }),
  );

  const localId = toInteger(
    company.canonicalCompanyId ?? (company as any).companyId ?? company.id,
  );
  if (localId && localId > 0) {
    push(`company:${localId}`);
    push(String(localId));
  }

  return aliases;
}

export function getCompanyIdFromSelectionToken(raw: unknown, source: CompanySource = 'company'): number | null {
  const parsed = parseCompanySelectionToken(raw);
  if (!parsed || parsed.source !== source) {
    return null;
  }
  return parsed.id;
}

export function buildCompanyDetailsHref(
  company: Pick<
    Company,
    | 'id'
    | 'idSource'
    | 'source'
    | 'theirstackId'
    | 'canonicalCompanyId'
    | 'resolvedCompanyId'
    | 'companyId'
  > | null | undefined,
  options?: { tab?: string },
): string {
  const localCompanyId = [
    company?.canonicalCompanyId,
    company?.companyId,
    company?.id,
  ]
    .map(toInteger)
    .find((id): id is number => Boolean(id && id > 0));
  if (localCompanyId) {
    const params = new URLSearchParams();
    if (options?.tab) {
      params.set('tab', options.tab);
    }
    const query = params.toString();
    return query
      ? `/company/${localCompanyId}?${query}`
      : `/company/${localCompanyId}`;
  }

  const token = getCompanySelectionToken(company);
  const parsed = token ? parseCompanySelectionToken(token) : null;
  if (!parsed) {
    return '/company';
  }

  const params = new URLSearchParams();
  if (options?.tab) {
    params.set('tab', options.tab);
  }

  const query = params.toString();
  return query ? `/company/${parsed.id}?${query}` : `/company/${parsed.id}`;
}
